import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { describeSegment, type ConditionEntry } from '@shared/segments'
import {
  ACTIONS_FOR_ENTITY,
  ACTION_LABELS,
  FLOW_TEMPLATES,
  TRIGGER_ENTITY,
  TRIGGER_LABELS,
  fieldsForTrigger,
  validateAutomationRule,
  type AutomationAction,
  type AutomationTrigger,
  type FlowTemplate,
} from '@shared/automationRules'
import { api, ApiError, type AutomationFlowStepInput, type AutomationFlowView } from '../lib/api'
import { SegmentBuilder } from './SegmentBuilder'
import { ActionListEditor, StepCard } from './AutomationRules'
import { AutomationCanvas, estimateCanvasHeight, type CanvasChain } from './AutomationCanvas'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/**
 * The no-code flow builder — a visual chain of steps sharing one trigger, wrapped around the
 * same rule engine `AutomationRules.tsx` already authors one rule at a time against. Every step
 * here is a real `automationRules` row (see tenantSchema.ts's `automationFlows`/`flowId`); this
 * screen only adds a way to author and view several of them as one connected flow. The engine
 * itself — `runAutomationRules` — never learns a flow exists.
 *
 * "Wait" is the one genuinely new-feeling primitive, and it isn't new: it's the same
 * `delaySeconds` field a hand-authored rule has always had, just given its own control per step
 * instead of once per rule. "Branch" has no native equivalent in the engine — there's no if/else
 * inside one rule — so a flow with a fork is really two (or more) steps with complementary
 * conditions, same trigger, same wait, different outcomes. That's a real, working substitute,
 * not a shortcut, but it's worth knowing it's what's happening under a "branch" label.
 */
interface StepDraft {
  name: string
  match: 'all' | 'any'
  conditions: ConditionEntry[]
  actions: AutomationAction[]
  enabled: boolean
  delayMinutes: number
}

function blankStep(): StepDraft {
  return { name: '', match: 'all', conditions: [], actions: [], enabled: true, delayMinutes: 0 }
}

export function FlowBuilder() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [startTemplate, setStartTemplate] = useState<FlowTemplate | null>(null)

  const flowsQuery = useQuery({ queryKey: ['automation-flows'], queryFn: api.automationFlows, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automation-flows'] })
  const remove = useMutation({ mutationFn: (id: string) => api.deleteAutomationFlow(id), onSuccess: refresh })

  if (flowsQuery.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (flowsQuery.isError) {
    const message = flowsQuery.error instanceof ApiError ? flowsQuery.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { flows, canManage } = flowsQuery.data!

  return (
    <>
      {!canManage && <ReadOnlyNotice what="flows" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Automation</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{flows.length} flow{flows.length === 1 ? '' : 's'}</h1>
          {canManage && !creating && !galleryOpen && !editingId && (
            <button type="button" className="cad-btn" onClick={() => setGalleryOpen(true)}>
              New flow
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          A chain of steps that share one trigger — welcome, wait, follow up — built without
          opening a rule's JSON. Every step is a real automation rule underneath, so flows and
          hand-authored rules run side by side, in the same priority order.
        </p>
      </header>

      {galleryOpen && (
        <FlowTemplateGallery
          onPick={(template) => {
            setStartTemplate(template)
            setGalleryOpen(false)
            setCreating(true)
          }}
          onBlank={() => {
            setStartTemplate(null)
            setGalleryOpen(false)
            setCreating(true)
          }}
          onCancel={() => setGalleryOpen(false)}
        />
      )}

      {creating && (
        <FlowEditor
          template={startTemplate}
          onDone={() => {
            setCreating(false)
            setStartTemplate(null)
            void refresh()
          }}
          onCancel={() => {
            setCreating(false)
            setStartTemplate(null)
          }}
        />
      )}

      <section className="cad-card">
        {remove.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)', marginBottom: '12px' }}>
            {remove.error instanceof ApiError || remove.error instanceof Error ? remove.error.message : 'That did not go through.'}
          </p>
        )}
        {flows.length === 0 ? (
          <p className="cad-muted">
            No flows yet. A flow is several rules in order, each waiting a set time before it
            checks &mdash; a three-step chase on an unanswered quote, say.{' '}
            {canManage
              ? '“New flow” above opens a short gallery, or you can start from a blank one.'
              : 'Your role cannot add them.'}
          </p>
        ) : (
          <div className="stack gap-4">
            {flows.map((flow) =>
              editingId === flow.id ? (
                <FlowEditor
                  key={flow.id}
                  flow={flow}
                  onDone={() => {
                    setEditingId(null)
                    void refresh()
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <FlowRow
                  key={flow.id}
                  flow={flow}
                  canManage={canManage}
                  onEdit={() => setEditingId(flow.id)}
                  onDelete={() => remove.mutate(flow.id)}
                />
              ),
            )}
          </div>
        )}
      </section>
    </>
  )
}

function FlowTemplateGallery({
  onPick,
  onBlank,
  onCancel,
}: {
  onPick: (template: FlowTemplate) => void
  onBlank: () => void
  onCancel: () => void
}) {
  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h2 className="cad-section-title">Start a flow</h2>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
        <div className="row gap-3 wrap">
          {FLOW_TEMPLATES.map((template) => (
            <button
              key={template.name}
              type="button"
              className="cad-card cad-flow-template"
              onClick={() => onPick(template)}
            >
              <div className="stack gap-1">
                <span style={{ fontWeight: 600 }}>{template.name}</span>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>{template.description}</span>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                  {template.steps.length} step{template.steps.length === 1 ? '' : 's'}
                </span>
              </div>
            </button>
          ))}
        </div>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={onBlank}>
          Start blank
        </button>
      </div>
    </section>
  )
}

function FlowRow({
  flow,
  canManage,
  onEdit,
  onDelete,
}: {
  flow: AutomationFlowView
  canManage: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [view, setView] = useState<'chain' | 'canvas'>('chain')
  const trigger = flow.steps[0]?.trigger
  const fields = trigger ? fieldsForTrigger(trigger) : []

  // One chain, its steps stacked in run order — a flow's steps are sequential, not a branch, so
  // they belong in a single column, not side by side (a real branch would be two *chains*).
  const chains: CanvasChain[] = [
    {
      id: flow.id,
      segments: flow.steps.map((step) => ({
        criteriaLabel:
          step.conditions.length > 0
            ? describeSegment({ match: step.match, conditions: step.conditions }, fields)
            : null,
        delayLabel: step.delaySeconds ? `Wait ${Math.round(step.delaySeconds / 60)}m` : undefined,
        disabled: !step.enabled,
        actions: step.actions.map((a, i) => ({ key: `${step.id}-${i}`, label: ACTION_LABELS[a.type] })),
      })),
    },
  ]

  return (
    <div className="cad-flow-row">
      <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
        <div className="stack gap-1">
          <span style={{ fontWeight: 600 }}>{flow.name}</span>
          {flow.description && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>{flow.description}</span>}
        </div>
        <div className="row gap-2">
          <button
            type="button"
            className="cad-btn"
            data-variant="text"
            onClick={() => setView((v) => (v === 'chain' ? 'canvas' : 'chain'))}
          >
            {view === 'chain' ? 'View as canvas' : 'View as list'}
          </button>
          {canManage && (
            <>
              <button type="button" className="cad-btn" data-variant="text" onClick={onEdit}>
                Edit
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                // Every step under a flow is a real automation rule; deleting the flow deletes all
                // of them, which the row's own "N steps" chip is the only hint of.
                onClick={() => {
                  const ok = confirm(
                    `Delete "${flow.name}"?\n\nAll ${flow.steps.length} step${flow.steps.length === 1 ? '' : 's'} under it are deleted with it. This cannot be undone.`,
                  )
                  if (ok) onDelete()
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {view === 'canvas' ? (
        <AutomationCanvas
          triggerLabel={trigger ? TRIGGER_LABELS[trigger] : 'No steps yet'}
          chains={chains}
          onNodeClick={canManage ? onEdit : undefined}
          height={estimateCanvasHeight(chains)}
        />
      ) : (
        <div className="cad-flow-chain">
          <div className="cad-flow-node cad-flow-trigger">
            {trigger ? TRIGGER_LABELS[trigger] : 'No steps yet'}
          </div>
          {flow.steps.map((step) => (
            <div key={step.id} className="cad-flow-chain-item">
              <span className="cad-flow-connector" aria-hidden="true" />
              <div className="cad-flow-node">
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  {Boolean(step.delaySeconds) && (
                    <span className="cad-pill" data-tone="accent">Wait {Math.round((step.delaySeconds ?? 0) / 60)}m</span>
                  )}
                  <span style={{ fontWeight: 500 }}>{step.name}</span>
                  {!step.enabled && <span className="cad-pill">Off</span>}
                </div>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                  {step.actions.length} action{step.actions.length === 1 ? '' : 's'}
                  {step.conditions.length > 0
                    ? ` · if ${describeSegment({ match: step.match, conditions: step.conditions }, fields)}`
                    : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FlowEditor({
  flow,
  template,
  onDone,
  onCancel,
}: {
  flow?: AutomationFlowView
  template?: FlowTemplate | null
  onDone: () => void
  onCancel: () => void
}) {
  const team = useQuery({ queryKey: ['team'], queryFn: api.team })
  const [name, setName] = useState(flow?.name ?? template?.name ?? '')
  const [description, setDescription] = useState(flow?.description ?? template?.description ?? '')
  const [trigger, setTrigger] = useState<AutomationTrigger>(
    flow?.steps[0]?.trigger ?? template?.trigger ?? 'message.received',
  )
  const [steps, setSteps] = useState<StepDraft[]>(() => {
    if (flow) {
      return flow.steps.map((s) => ({
        name: s.name,
        match: s.match,
        conditions: s.conditions,
        actions: s.actions,
        enabled: s.enabled,
        delayMinutes: Math.round((s.delaySeconds ?? 0) / 60),
      }))
    }
    if (template) {
      return template.steps.map((s) => ({
        name: s.name ?? '',
        match: s.match,
        conditions: s.conditions,
        actions: s.actions,
        enabled: true,
        delayMinutes: s.delayMinutes ?? 0,
      }))
    }
    return [blankStep()]
  })

  const fields = fieldsForTrigger(trigger).map((f) =>
    f.key === 'labels' && team.data?.labels.length ? { ...f, options: team.data.labels.map((l) => l.name) } : f,
  )
  const allowedActions = ACTIONS_FOR_ENTITY[TRIGGER_ENTITY[trigger]]

  const stepInputs: AutomationFlowStepInput[] = steps.map((s) => ({
    name: s.name.trim() || undefined,
    match: s.match,
    conditions: s.conditions,
    actions: s.actions,
    enabled: s.enabled,
    delayMinutes: s.delayMinutes,
  }))
  const stepProblems = steps.flatMap((s, index) =>
    validateAutomationRule({
      name: s.name.trim() || name,
      trigger,
      match: s.match,
      conditions: s.conditions,
      actions: s.actions,
      enabled: s.enabled,
    }).map((problem) => `Step ${index + 1}: ${problem}`),
  )
  const problems = [...(name.trim() ? [] : ['Give the flow a name.']), ...stepProblems]

  const save = useMutation({
    mutationFn: () =>
      flow
        ? api.updateAutomationFlow(flow.id, { name, description: description || null, trigger, steps: stepInputs })
        : api.createAutomationFlow({ name, description: description || undefined, trigger, steps: stepInputs }),
    onSuccess: onDone,
  })

  const error = save.error
  const errorMessage = error instanceof ApiError || error instanceof Error ? error.message : undefined

  const updateStep = (index: number, patch: Partial<StepDraft>) =>
    setSteps((current) => current.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  const removeStep = (index: number) => setSteps((current) => current.filter((_, i) => i !== index))
  const addStep = () => setSteps((current) => [...current, blankStep()])

  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <h2 className="cad-section-title">{flow ? 'Edit flow' : 'New flow'}</h2>

        <div className="field-grid">
          <div className="cad-field">
            <label htmlFor="flow-name">Flow name</label>
            <input id="flow-name" className="cad-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="cad-field">
            <label htmlFor="flow-trigger">Starts when</label>
            <select
              id="flow-trigger"
              className="cad-input"
              value={trigger}
              onChange={(e) => {
                const next = e.target.value as AutomationTrigger
                setTrigger(next)
                setSteps((current) => current.map((s) => ({ ...s, conditions: [], actions: [] })))
              }}
            >
              {(Object.keys(TRIGGER_LABELS) as AutomationTrigger[]).map((t) => (
                <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="cad-field">
          <label htmlFor="flow-description">Description (optional)</label>
          <input
            id="flow-description"
            className="cad-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="cad-flow-canvas">
          <div className="cad-flow-node cad-flow-trigger">{TRIGGER_LABELS[trigger]}</div>

          {steps.map((step, index) => (
            <div key={index} className="cad-flow-chain-item cad-flow-chain-item-editor">
              <span className="cad-flow-connector" aria-hidden="true" />
              <StepCard
                icon="action"
                title={`Step ${index + 1}`}
                description="Optionally wait, check conditions, then run these actions"
              >
                <div className="stack gap-3">
                  <div className="field-grid">
                    <div className="cad-field">
                      <label htmlFor={`step-name-${index}`}>Step name (optional)</label>
                      <input
                        id={`step-name-${index}`}
                        className="cad-input"
                        value={step.name}
                        placeholder={`Step ${index + 1}`}
                        onChange={(e) => updateStep(index, { name: e.target.value })}
                      />
                    </div>
                    <div className="cad-field" style={{ maxWidth: '220px' }}>
                      <label htmlFor={`step-delay-${index}`}>Wait before this step (minutes)</label>
                      <input
                        id={`step-delay-${index}`}
                        className="cad-input"
                        type="number"
                        min={0}
                        value={step.delayMinutes}
                        onChange={(e) => updateStep(index, { delayMinutes: Math.max(0, Number(e.target.value) || 0) })}
                      />
                    </div>
                  </div>

                  <div className="stack gap-2">
                    <span className="cad-label">Conditions</span>
                    {step.conditions.length === 0 ? (
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="quiet"
                        onClick={() => updateStep(index, { conditions: [{ field: fields[0].key, operator: 'is', value: '' }] })}
                      >
                        Add condition
                      </button>
                    ) : (
                      <SegmentBuilder
                        fields={fields}
                        match={step.match}
                        conditions={step.conditions}
                        onChange={(next) => updateStep(index, { match: next.match, conditions: next.conditions })}
                      />
                    )}
                  </div>

                  <div className="stack gap-2">
                    <span className="cad-label">Actions</span>
                    <ActionListEditor
                      actions={step.actions}
                      allowedActions={allowedActions}
                      onChange={(next) => updateStep(index, { actions: next })}
                      members={team.data?.members}
                    />
                  </div>

                  {steps.length > 1 && (
                    <button type="button" className="cad-btn" data-variant="text" onClick={() => removeStep(index)}>
                      Remove step
                    </button>
                  )}
                </div>
              </StepCard>
            </div>
          ))}

          <div className="cad-flow-chain-item">
            <span className="cad-flow-connector" aria-hidden="true" />
            <button type="button" className="cad-btn" data-variant="quiet" onClick={addStep}>
              + Add step
            </button>
          </div>
        </div>

        {problems.length > 0 && (
          <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{problems[0]}</p>
        )}
        {errorMessage && <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{errorMessage}</p>}

        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-btn"
            disabled={problems.length > 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save flow'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}
